import { useState, useEffect } from 'react'
import { useAppStore } from '@/store/appStore'
import { supabase } from '@/lib/supabase'
import { invokeEdgeFunction } from '@/lib/edgeFunctions'
import { ASSESSMENT_FIELDS, ASSESSMENT_LABELS } from '@/lib/assessment-engine'
import type { WatchlistColumnType } from '@/types'

interface AddStockModalProps {
  onClose: () => void;
}

export function AddStockModal({ onClose }: AddStockModalProps) {
  const categories = useAppStore(s => s.categories)
  const stocks = useAppStore(s => s.stocks)
  const addStock = useAppStore(s => s.addStock)
  const addCategory = useAppStore(s => s.addCategory)
  const customColumns = useAppStore(s => s.customColumns)
  const upsertCustomValue = useAppStore(s => s.upsertCustomValue)
  const updateOverride = useAppStore(s => s.updateOverride)

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [ticker, setTicker] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [lookupResult, setLookupResult] = useState<{ symbol: string; name: string; exchange: string } | null>(null)
  const [aiCategory, setAiCategory] = useState<{ category: string; isNewCategory: boolean; assessment?: any } | null>(null)
  const [categorizing, setCategorizing] = useState(false)
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [selectedCategory, setSelectedCategory] = useState('')

  // Trigger AI categorization when lookupResult changes
  useEffect(() => {
    if (!lookupResult) { setAiCategory(null); return }
    setCategorizing(true)
    invokeEdgeFunction<any>('ai-categorize', {
      method: 'POST',
      body: { ticker: lookupResult.symbol, name: lookupResult.name },
    })
      .then(data => {
        if (data.category) setAiCategory(data)
      })
      .catch(() => {})
      .finally(() => setCategorizing(false))
  }, [lookupResult])

  // Lookup ticker via Supabase watchlist or validate format
  const handleLookup = async () => {
    if (!ticker.trim()) {
      setError('Enter a ticker symbol')
      return
    }

    // Check for duplicate
    const existingTickers = Object.values(stocks).map(s => s.ticker.toUpperCase())
    if (existingTickers.includes(ticker.trim().toUpperCase())) {
      setError(`${ticker.trim().toUpperCase()} is already in your watchlist`)
      return
    }

    setLoading(true)
    setError('')
    try {
      // Validate ticker format (1-10 uppercase alphanumeric chars)
      const tickerVal = ticker.trim().toUpperCase()
      if (!/^[A-Z][A-Z0-9.-]{0,9}$/.test(tickerVal)) {
        setError('Invalid ticker format. Use uppercase letters, numbers, dots, or hyphens (e.g. AAPL, BRK.B)')
        setLoading(false)
        return
      }
      // Use the ticker directly — no search API in v2
      setLookupResult({ symbol: tickerVal, name: tickerVal, exchange: '' })
      setStep(2)
    } catch {
      setError('Failed to look up symbol. Check your connection.')
    } finally {
      setLoading(false)
    }
  }

  // Auto-categorize based on sector/name keywords (fallback)
  const autoCategory = (name: string): string => {
    const lower = name.toLowerCase()
    if (lower.includes('semiconductor') || lower.includes('chip') || lower.includes('nvidia') || lower.includes('amd')) return 'semi'
    if (lower.includes('cloud') || lower.includes('amazon') || lower.includes('google') || lower.includes('microsoft') || lower.includes('meta')) return 'cloud'
    if (lower.includes('software') || lower.includes('data') || lower.includes('security') || lower.includes('cyber')) return 'ai-software'
    if (lower.includes('server') || lower.includes('hardware') || lower.includes('dell') || lower.includes('hewlett')) return 'ai-hardware'
    if (lower.includes('network') || lower.includes('optical') || lower.includes('fiber') || lower.includes('photon')) return 'optical'
    if (lower.includes('power') || lower.includes('energy') || lower.includes('electric') || lower.includes('nuclear')) return 'power'
    if (lower.includes('memory') || lower.includes('storage') || lower.includes('disk') || lower.includes('nand')) return 'memory'
    if (lower.includes('apple') || lower.includes('netflix') || lower.includes('uber') || lower.includes('health')) return 'consumer-ai'
    if (lower.includes('bank') || lower.includes('finance') || lower.includes('payment') || lower.includes('fintech')) return 'fintech'
    if (lower.includes('etf') || lower.includes('trust') || lower.includes('index') || lower.includes('spdr')) return 'etf'
    if (lower.includes('quantum')) return 'quantum'
    if (lower.includes('robot') || lower.includes('automat') || lower.includes('tesla')) return 'robotics'
    return '' // No match — will create "Other" category
  }

  const handleAdd = async () => {
    if (!lookupResult) return

    setLoading(true)
    setError('')

    try {
      let categoryId = selectedCategory || '' // Use user's explicit selection if set
      const suggestedName = aiCategory?.category || ''

      if (!categoryId && suggestedName) {
        // Find matching category by name (case-insensitive)
        const match = categories.find(c => c.name.toLowerCase().includes(suggestedName.toLowerCase()) || suggestedName.toLowerCase().includes(c.name.toLowerCase()))
        if (match) {
          categoryId = match.id
        } else if (aiCategory?.isNewCategory) {
          // Create the new category
          await addCategory(suggestedName)
          const cats = useAppStore.getState().categories
          categoryId = cats[cats.length - 1]?.id || ''
        }
      }

      // Fallback to autoCategory or "Other"
      if (!categoryId) {
        categoryId = autoCategory(lookupResult.name)
      }
      if (!categoryId) {
        const otherCat = categories.find(c => c.name.toLowerCase().includes('other'))
        if (otherCat) {
          categoryId = otherCat.id
        } else {
          await addCategory('Other')
          const cats = useAppStore.getState().categories
          categoryId = cats[cats.length - 1]?.id || categories[0]?.id || ''
        }
      }

      if (!categories.find(c => c.id === categoryId)) {
        categoryId = categories[0]?.id || ''
      }

      await addStock(lookupResult.symbol, lookupResult.name, categoryId)

      // Get the newly created stock id
      const newStock = Object.values(useAppStore.getState().stocks).find(
        s => s.ticker === lookupResult.symbol.toUpperCase()
      )
      const newStockId = newStock?.id || ''

      // Apply field values if we have a stock id
      if (newStockId) {
        for (const [key, value] of Object.entries(fieldValues)) {
          if (!value.trim()) continue
          // Check if it's an assessment field
          if ((ASSESSMENT_FIELDS as readonly string[]).includes(key)) {
            updateOverride(newStockId, key, value.trim())
          } else {
            // Custom column value — key is the column id
            await upsertCustomValue(newStockId, key, value.trim())
          }
        }
      }

      onClose()
    } catch {
      setError('Failed to add stock')
    } finally {
      setLoading(false)
    }
  }

  const setFieldValue = (key: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [key]: value }))
  }

  // Render an input for a custom column based on its type
  const renderCustomColumnInput = (colId: string, type: WatchlistColumnType, options: string[]) => {
    const value = fieldValues[colId] || ''

    if (type === 'dropdown') {
      return (
        <select
          value={value}
          onChange={e => setFieldValue(colId, e.target.value)}
          className="w-full px-2 py-1.5 text-sm bg-white border border-[var(--color-border)] rounded text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none"
        >
          <option value="">—</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )
    }

    if (type === 'multiselect') {
      // Simple comma-joined text input for multiselect
      return (
        <input
          type="text"
          value={value}
          onChange={e => setFieldValue(colId, e.target.value)}
          placeholder={options.length > 0 ? `e.g. ${options.slice(0, 2).join(', ')}` : 'Comma-separated values'}
          className="w-full px-2 py-1.5 text-sm bg-white border border-[var(--color-border)] rounded text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none"
        />
      )
    }

    if (type === 'number') {
      return (
        <input
          type="number"
          value={value}
          onChange={e => setFieldValue(colId, e.target.value)}
          className="w-full px-2 py-1.5 text-sm bg-white border border-[var(--color-border)] rounded text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none"
        />
      )
    }

    // Default: text
    return (
      <input
        type="text"
        value={value}
        onChange={e => setFieldValue(colId, e.target.value)}
        className="w-full px-2 py-1.5 text-sm bg-white border border-[var(--color-border)] rounded text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none"
      />
    )
  }

  // Notes field is full-width textarea — other assessment fields are text inputs
  const renderAssessmentInput = (field: string) => {
    const value = fieldValues[field] || ''

    if (field === 'notes') {
      return (
        <textarea
          value={value}
          onChange={e => setFieldValue(field, e.target.value)}
          rows={2}
          className="w-full px-2 py-1.5 text-sm bg-white border border-[var(--color-border)] rounded text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none resize-none"
        />
      )
    }

    return (
      <input
        type="text"
        value={value}
        onChange={e => setFieldValue(field, e.target.value)}
        className="w-full px-2 py-1.5 text-sm bg-white border border-[var(--color-border)] rounded text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none"
      />
    )
  }

  const isStep3 = step === 3

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6" onClick={onClose}>
      <div
        className={`bg-white border border-[var(--color-border)] rounded-xl p-8 w-full shadow-xl transition-all max-h-[90vh] flex flex-col ${isStep3 ? 'max-w-2xl' : 'max-w-lg'}`}
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-[var(--color-text-primary)] mb-6">Add Stock to Watchlist</h2>

        {step === 1 && (
          /* Step 1: Enter ticker */
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Ticker Symbol or Company Name</label>
              <input
                type="text"
                value={ticker}
                onChange={e => { setTicker(e.target.value.toUpperCase()); setError('') }}
                onKeyDown={e => e.key === 'Enter' && handleLookup()}
                placeholder="e.g. AAPL or Apple"
                className="w-full px-4 py-3 bg-white border border-[var(--color-border)] rounded-lg text-[var(--color-text-primary)] text-lg focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] outline-none"
                style={{ height: '56px' }}
                autoFocus
              />
            </div>

            {error && <p className="text-sm text-[var(--color-red)] font-medium">{error}</p>}

            <div className="flex justify-end gap-3 pt-6 border-t border-[var(--color-border)]">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 text-sm font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
                style={{ height: '48px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleLookup}
                disabled={loading}
                className="px-6 py-3 text-sm font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/80 disabled:opacity-50"
                style={{ height: '48px' }}
              >
                {loading ? 'Looking up...' : 'Look Up'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && lookupResult && (
          /* Step 2: Confirm stock */
          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-[var(--color-accent)]">{lookupResult.symbol}</span>
                <span className="text-sm text-[var(--color-text-muted)]">{lookupResult.exchange}</span>
              </div>
              <p className="text-[var(--color-text-primary)] mt-1">{lookupResult.name}</p>
              <p className="text-xs text-[var(--color-text-muted)] mt-2">
                Category:
                <select
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  className="ml-2 px-2 py-1 text-xs border border-[var(--color-border)] rounded bg-white text-[var(--color-text-primary)] focus:border-[var(--color-accent)] outline-none"
                >
                  <option value="">{categorizing ? '🤖 Analyzing...' : (aiCategory?.category || autoCategory(lookupResult.name) || 'Other')} (auto)</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {aiCategory?.isNewCategory && !selectedCategory && <span className="ml-1 text-[var(--color-accent)]">(new)</span>}
              </p>
            </div>

            {error && <p className="text-sm text-[var(--color-red)] font-medium">{error}</p>}

            <div className="flex justify-end gap-3 pt-6 border-t border-[var(--color-border)]">
              <button
                type="button"
                onClick={() => { setLookupResult(null); setTicker(''); setStep(1) }}
                className="px-6 py-3 text-sm font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
                style={{ height: '48px' }}
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={loading}
                className="px-6 py-3 text-sm font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/80 disabled:opacity-50"
                style={{ height: '48px' }}
              >
                Next: Fill Details →
              </button>
            </div>
          </div>
        )}

        {step === 3 && lookupResult && (
          /* Step 3: Fill in optional field values */
          <div className="flex flex-col min-h-0 flex-1">
            {/* Stock summary at top */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)] shrink-0">
              <span className="text-lg font-bold text-[var(--color-accent)]">{lookupResult.symbol}</span>
              <span className="text-sm text-[var(--color-text-primary)]">{lookupResult.name}</span>
              <span className="ml-auto text-xs text-[var(--color-text-muted)]">All fields optional</span>
            </div>

            {/* Scrollable fields area */}
            <div className="flex-1 overflow-y-auto mt-4 space-y-6 pr-1">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-3">Assessment Fields</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {ASSESSMENT_FIELDS.filter(f => f !== 'notes').map(field => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                      {ASSESSMENT_LABELS[field]}
                    </label>
                    {renderAssessmentInput(field)}
                  </div>
                ))}
                {/* Notes spans full width */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                    {ASSESSMENT_LABELS['notes']}
                  </label>
                  {renderAssessmentInput('notes')}
                </div>
              </div>
            </div>

            {/* Custom Columns (only shown if any exist) */}
            {customColumns.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-3">Custom Fields</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {customColumns.map(col => (
                    <div key={col.id}>
                      <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                        {col.name}
                        {col.type === 'multiselect' && (
                          <span className="ml-1 text-[var(--color-text-muted)] font-normal">(comma-separated)</span>
                        )}
                      </label>
                      {renderCustomColumnInput(col.id, col.type, col.options)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            </div>{/* end scrollable area */}

            {error && <p className="text-sm text-[var(--color-red)] font-medium shrink-0 mt-3">{error}</p>}

            <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-border)] mt-4 shrink-0">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-6 py-3 text-sm font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
                style={{ height: '48px' }}
              >
                ← Back
              </button>
              <button
                onClick={handleAdd}
                disabled={loading}
                className="px-6 py-3 text-sm font-medium rounded-lg bg-[var(--color-green)] text-white hover:bg-[var(--color-green)]/80 disabled:opacity-50"
                style={{ height: '48px' }}
              >
                {loading ? 'Adding...' : `Add ${lookupResult.symbol} to Watchlist`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
